import React, { useEffect, useRef, useContext } from 'react';
import ReactFlow, { Background, useReactFlow } from 'reactflow';
import 'reactflow/dist/style.css';
import CanvasContext from '../../../context/CanvasContext';
import ClassroomCanvasNode from './ClassroomCanvasNode';
import ClassroomCanvasEdge from './ClassroomCanvasEdge';
import StudentPositionsContext from './StudentPositionsContext';
import styles from './styles/ClassroomCanvas.module.css';
import socket from './../../../socket';
import { FaLock, FaUnlock } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';

const NO_STUDENTS = [];

// Reads live data from context instead of closing over props, so nodeTypes
// below can be a module-level constant. Recreating nodeTypes per render makes
// ReactFlow remount every node on every student move — the main perf bug this
// component had.
const ClassroomCanvasNodeWrapper = (nodeProps) => {
    const { selectedNodeId } = useContext(CanvasContext);
    const studentPositions = useContext(StudentPositionsContext);

    const students = studentPositions[nodeProps.id] || NO_STUDENTS;
    const isSelected = selectedNodeId === nodeProps.id;

    return <ClassroomCanvasNode {...nodeProps} selected={isSelected} students={students} />;
};

const nodeTypes = {
    situation: ClassroomCanvasNodeWrapper,
};

const edgeTypes = {
    choice: ClassroomCanvasEdge,
    smart: ClassroomCanvasEdge,
};

const ClassroomCanvas = ({ assignmentId, studentPositions, universalLock, setUniversalLock, selectedUserPropFunction }) => {
    const {
        nodes,
        edges,
        loading,
        selectedNodeId,
        selectedEdgeId,
        onSelectNodeClassroom,
        onSelectEdgeClassroom,
        onCanvasClickClassroom,
        resetHighlightSubmission,
    } = useContext(CanvasContext);
    const reactFlowWrapper = useRef(null);
    const { getZoom, setCenter } = useReactFlow();

    useEffect(() => {
        if (!loading && nodes.length > 0) {
            const startNode = nodes.find((node) => node.data?.endpointType === 'start');
            const nodeToCenter = startNode || nodes[0];

            if (nodeToCenter && nodeToCenter.position) {
                const { x, y } = nodeToCenter.position;
                const zoom = getZoom();
                setCenter(x, y, { zoom, duration: 800 });
            }
        }
    }, [loading, nodes, getZoom, setCenter]);

    const onNodeClick = (event, node) => {
        event.stopPropagation();
        if (selectedNodeId === node.id) {
            onCanvasClickClassroom();
        } else {
            onSelectNodeClassroom(node.id);
        }
    };

    const onEdgeClick = (event, edge) => {
        event.stopPropagation();
        if (selectedEdgeId === edge.id) {
            onCanvasClickClassroom();
        } else {
            onSelectEdgeClassroom(edge.id);
        }
    };

    const toggleUniversalLock = () => {
        socket.emit('toggleUniversalLock', { universalLock: !universalLock, assignmentId });
        setUniversalLock(!universalLock);
    }

    const handleCanvasClick = () => {
        onCanvasClickClassroom();
        selectedUserPropFunction(null);
        resetHighlightSubmission();
    }

    if (loading) {
        return <div className={styles.loading}>Loading Canvas...</div>;
    }

    return (
        <div
            className={styles.container}
            ref={reactFlowWrapper}
            onDragOver={(event) => event.preventDefault()}
        >
            <div className={styles.controlsBar}>
                <button
                    onClick={toggleUniversalLock}
                    className={`${styles.lockButton} ${universalLock ? styles.locked : ''}`}
                    data-tooltip-id="lock-tooltip"
                    data-tooltip-content={universalLock ? 'Unlock scenario progress' : 'Lock scenario progress'}
                    aria-label={universalLock ? 'Unlock scenario progress' : 'Lock scenario progress'}
                >

                    {universalLock ? <FaLock /> : <FaUnlock />}
                </button>
            </div>

            <StudentPositionsContext.Provider value={studentPositions}>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    onNodeClick={onNodeClick}
                    onEdgeClick={onEdgeClick}
                    onPaneClick={handleCanvasClick}
                    deleteKeyCode={null}
                >
                    <Background color="#aaa" gap={16} />
                </ReactFlow>
            </StudentPositionsContext.Provider>

            <Tooltip id="lock-tooltip" place="bottom" effect="solid" />
        </div>
    );
};

export default ClassroomCanvas;
